// src/utils/clipboard.ts
//
// Kopírování textu do schránky (DetailRow copyable – číslo účtu, VS, částka).
// navigator.clipboard vyžaduje zabezpečený kontext a ve starším Android
// WebView (Expo obal) chybí nebo požadavek odmítne – proto záložní cesta přes
// skrytou textareu a document.execCommand("copy").

import { reportWarning } from "./reportError";

/** Záložní kopírování přes dočasnou textareu; vrací true při úspěchu. */
function copyWithTextarea(text: string): boolean {
  if (typeof document === "undefined" || !document.body) return false;
  const previousFocus = document.activeElement as HTMLElement | null;
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.setAttribute("aria-hidden", "true");
  // Mimo obraz; 16 px, aby iOS při fokusu nezoomoval stránku.
  textarea.style.position = "fixed";
  textarea.style.top = "0";
  textarea.style.left = "-9999px";
  textarea.style.fontSize = "16px";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);

  let ok = false;
  try {
    textarea.select();
    textarea.setSelectionRange(0, text.length);
    ok = document.execCommand("copy");
  } catch (err) {
    reportWarning("copyToClipboard.textarea", err);
    ok = false;
  } finally {
    document.body.removeChild(textarea);
    // Fokus zpět na tlačítko – klávesnice ani čtečka nepřijdou o pozici.
    if (previousFocus && typeof previousFocus.focus === "function") {
      try {
        previousFocus.focus();
      } catch {
        /* fokus je jen kosmetika */
      }
    }
  }
  return ok;
}

/**
 * Zkopíruje text do schránky. Nejdřív navigator.clipboard, při jeho
 * nedostupnosti nebo odmítnutí záložní textarea. Nikdy nevyhazuje;
 * vrací true, když se kopírování (pravděpodobně) povedlo.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (
      typeof navigator !== "undefined" &&
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === "function" &&
      (typeof window === "undefined" || window.isSecureContext !== false)
    ) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (err) {
    // WebView bez oprávnění, nefokusovaný dokument… – zkusit záložní cestu.
    reportWarning("copyToClipboard.clipboardApi", err);
  }
  return copyWithTextarea(text);
}

export default copyToClipboard;
